import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Instagram, Globe, Save, Trash2, ExternalLink, User } from "lucide-react";
import { toast } from "sonner";
import api, { FILE_BASE } from "../lib/api";
import { isLoggedIn, getRole, setAuth, getToken, getUser } from "../lib/auth";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function PhotographerProfile() {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const logoInput = useRef(null);
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [bioShort, setBioShort] = useState("");
  const [bio, setBio] = useState("");
  const [ig, setIg] = useState("");
  const [web, setWeb] = useState("");
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (!isLoggedIn() || getRole() !== "photographer") {
      navigate("/photographer/login");
      return;
    }
    api.get("/auth/me").then((r) => {
      setMe(r.data);
      setName(r.data.name || "");
      setBioShort(r.data.bio_short || "");
      setBio(r.data.bio || "");
      setIg(r.data.instagram_url || "");
      setWeb(r.data.website_url || "");
      setSlug(r.data.slug || "");
    }).catch(() => {});
  }, [navigate]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/photographers/me", {
        name: name.trim() || undefined,
        bio_short: bioShort.slice(0, 80),
        bio: bio.slice(0, 600),
        instagram_url: ig,
        website_url: web,
        slug: slug || undefined,
      });
      setMe(r.data);
      setName(r.data.name || "");
      setSlug(r.data.slug || "");
      // Refresh the local user cache so other parts of app know
      setAuth(getToken(), { ...(getUser() || {}), ...r.data }, "photographer");
      toast.success("Profilen sparad!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB profilbild");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/photographers/me/profile-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMe((m) => ({ ...m, profile_image_url: r.data.profile_image_url }));
      toast.success("Profilbild uppdaterad");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ladda upp");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async () => {
    if (!window.confirm("Ta bort profilbilden?")) return;
    await api.delete("/photographers/me/profile-image");
    setMe((m) => ({ ...m, profile_image_url: null }));
    toast.success("Borttagen");
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB logga");
      return;
    }
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/photographers/me/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMe((m) => ({ ...m, logo_image_url: r.data.logo_image_url }));
      toast.success("Logga uppladdad");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ladda upp");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Ta bort loggan?")) return;
    await api.delete("/photographers/me/logo");
    setMe((m) => ({ ...m, logo_image_url: null }));
    toast.success("Borttagen");
  };

  if (!me) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto p-12 text-white/50">Laddar...</div>
        <Footer />
      </div>
    );
  }

  const publicUrl = slug ? `/fotograf/${slug}` : null;

  return (
    <div className="App">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/photographer/dashboard" className="text-brand hover:underline text-sm mb-4 inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Tillbaka
        </Link>
        <p className="label-overline mb-2">Min profil</p>
        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl mb-6">
          Fotografprofil
        </h1>

        {publicUrl && (
          <div className="card-surface p-3 mb-6 flex items-center justify-between">
            <span className="text-sm text-white/60">
              Publik URL: <span className="text-brand font-mono">{publicUrl}</span>
            </span>
            <Link to={publicUrl} target="_blank" className="text-sm text-brand hover:underline inline-flex items-center gap-1" data-testid="public-profile-link">
              Förhandsgranska <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        )}

        {/* Profile image */}
        <section className="card-surface p-6 mb-6">
          <p className="label-overline mb-3">Profilbild</p>
          <div className="flex items-center gap-6">
            <div className="w-28 h-28 bg-[#1a1a1a] overflow-hidden border border-white/10 flex items-center justify-center">
              {me.profile_image_url ? (
                <img
                  src={`${FILE_BASE}${me.profile_image_url}`}
                  alt=""
                  className="w-full h-full object-cover"
                  data-testid="profile-image-preview"
                />
              ) : (
                <User className="w-10 h-10 text-white/20" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadImage(e.target.files?.[0])}
                data-testid="profile-image-input"
              />
              <button
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="btn-secondary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="profile-image-upload"
              >
                <Camera className="w-4 h-4" />
                {uploading ? "Laddar upp..." : me.profile_image_url ? "Byt bild" : "Ladda upp"}
              </button>
              {me.profile_image_url && (
                <button
                  onClick={removeImage}
                  className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                  data-testid="profile-image-remove"
                >
                  <Trash2 className="w-3 h-3" /> Ta bort
                </button>
              )}
              <p className="text-xs text-white/40">JPG/PNG · beskärs till kvadrat (512×512) · max 5 MB</p>
            </div>
          </div>
        </section>

        {/* Logo (optional) */}
        <section className="card-surface p-6 mb-6">
          <p className="label-overline mb-1">Logga (frivilligt)</p>
          <p className="text-xs text-white/50 mb-4">
            Visas som liten badge i nedre vänstra hörnet på alla dina foton — så kunderna ser tydligt vem som tagit bilden. Använd helst en PNG med transparent bakgrund.
          </p>
          <div className="flex items-center gap-6">
            <div className="w-32 h-16 bg-checker overflow-hidden border border-white/10 flex items-center justify-center" style={{ backgroundImage: 'linear-gradient(45deg, #2a2a2a 25%, transparent 25%), linear-gradient(-45deg, #2a2a2a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #2a2a2a 75%), linear-gradient(-45deg, transparent 75%, #2a2a2a 75%)', backgroundSize: '12px 12px', backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px' }}>
              {me.logo_image_url ? (
                <img
                  src={`${FILE_BASE}${me.logo_image_url}`}
                  alt="Logga"
                  className="max-h-14 max-w-28 object-contain"
                  data-testid="logo-preview"
                />
              ) : (
                <span className="text-xs text-white/30 px-2 text-center">Ingen logga</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={logoInput}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="logo-input"
              />
              <button
                onClick={() => logoInput.current?.click()}
                disabled={uploadingLogo}
                className="btn-secondary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="logo-upload"
              >
                <Camera className="w-4 h-4" />
                {uploadingLogo ? "Laddar upp..." : me.logo_image_url ? "Byt logga" : "Ladda upp"}
              </button>
              {me.logo_image_url && (
                <button
                  onClick={removeLogo}
                  className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                  data-testid="logo-remove"
                >
                  <Trash2 className="w-3 h-3" /> Ta bort
                </button>
              )}
              <p className="text-xs text-white/40">PNG (transparent bg) rekommenderas · max 5 MB · skalas till 400px</p>
            </div>
          </div>
        </section>

        {/* Text fields */}
        <section className="card-surface p-6 mb-6 space-y-5">
          <div>
            <label className="label-overline block mb-1">Namn / Visningsnamn *</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field"
              placeholder="Ex: Joakim Andersson Foto"
              data-testid="profile-name"
            />
            <p className="text-xs text-white/40 mt-1">Visas på dina foton, sökresultat och din publika profil.</p>
          </div>

          <div>
            <label className="label-overline block mb-1">URL-namn (slug)</label>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-sm">/fotograf/</span>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                className="input-field flex-1 font-mono"
                placeholder="ditt-namn"
                data-testid="profile-slug"
              />
            </div>
            <p className="text-xs text-white/40 mt-1">Bara små bokstäver, siffror och bindestreck.</p>
          </div>

          <div>
            <label className="label-overline block mb-1">Kort beskrivning ({bioShort.length}/80)</label>
            <input
              value={bioShort}
              onChange={(e) => setBioShort(e.target.value.slice(0, 80))}
              className="input-field"
              placeholder="Ex: Sportfotograf med fokus på enduro & motocross"
              data-testid="profile-bio-short"
            />
            <p className="text-xs text-white/40 mt-1">Visas under ditt namn på den publika profilen.</p>
          </div>

          <div>
            <label className="label-overline block mb-1">Om mig ({bio.length}/600)</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 600))}
              rows={5}
              className="input-field resize-none"
              placeholder="Berätta om dig själv, din kamera-utrustning, vilka event du brukar fota..."
              data-testid="profile-bio"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-1">Instagram</label>
              <div className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-brand" />
                <input
                  value={ig}
                  onChange={(e) => setIg(e.target.value)}
                  className="input-field"
                  placeholder="https://instagram.com/dittnamn"
                  data-testid="profile-ig"
                />
              </div>
            </div>
            <div>
              <label className="label-overline block mb-1">Hemsida</label>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-brand" />
                <input
                  value={web}
                  onChange={(e) => setWeb(e.target.value)}
                  className="input-field"
                  placeholder="https://dinhemsida.se"
                  data-testid="profile-web"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="flex gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="profile-save"
          >
            <Save className="w-4 h-4" /> {saving ? "Sparar..." : "Spara profilen"}
          </button>
          <Link to="/photographer/dashboard" className="btn-secondary">
            Avbryt
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
